import type { ReactNode } from 'react';
import { createBrowserRouter, Outlet, type RouteObject } from 'react-router';
import { ROUTER_BASENAME } from '@/app-base';
import {
  RequireAuth,
  RequireConsent,
  RequireFreshPassword,
  RequireOrgAdmin,
  RequireOrganization,
  RequirePermission,
  RequireZone,
} from '@/auth/guards';
import { ZONE_PATHS, ZONES, type Zone } from '@/auth/zone-home';
import { AppShell } from '@/layout/AppShell';
import { ZONE_MENUS, ZONE_TITLE_KEYS, type MenuItem } from '@/layout/menus';
import { LoginScreen } from '@/features/auth/LoginScreen';
import { FirstLoginSetup } from '@/features/auth/FirstLoginSetup';
import { ConsentWizard } from '@/features/legal/ConsentWizard';
import { PlaceholderPage } from '@/routes/Placeholder';
import { EXTRA_ROUTES, SCREENS } from './screens';
import { RootRedirect } from '@/routes/RootRedirect';
import { NotFoundRoute } from '@/routes/NotFound';

/**
 * react-router v7 KITAPLIK KIPI (framework/SSR kipi DEGIL).
 *
 * ROTA AGACININ SEKLI = KORUMA ZINCIRI.
 *
 *   /giris                       herkese acik
 *   /ilk-giris, /sozlesmeler     OTURUM ISTER ama kapilarin KENDISI oldugu icin
 *                                kapi kontrolunden gecmez (aksi halde kendine
 *                                sonsuz yonlendirme yapardi)
 *   /musteri, /buro, /yonetim    tam zincir + AppShell
 *
 * Bolge rotalari MENU VERISINDEN uretiliyor (bkz. src/layout/menus.ts). Elle
 * yazilsalardi menude olup rotasi olmayan (404) ya da rotasi olup menude
 * gorunmeyen ogeler kacinilmaz olurdu; tek kaynak bunu imkansiz kilar.
 */

/** Bolge yollarinin, react-router'in bekledigi GORELI hale getirilmesi. */
const relativePath = (zone: Zone, item: MenuItem): string | null => {
  const base = ZONE_PATHS[zone];
  if (item.to === base) return null; // index rotasi
  return item.to.startsWith(`${base}/`) ? item.to.slice(base.length + 1) : item.to;
};

/** Ogenin gorunurluk kurali, RET PANELI olarak rotaya sarilir. */
const withItemGuard = (item: MenuItem, element: ReactNode): ReactNode => {
  if (item.adminOnly) return <RequireOrgAdmin>{element}</RequireOrgAdmin>;
  const guarded = item.permission
    ? <RequirePermission code={item.permission}>{element}</RequirePermission>
    : element;
  return item.requiresOrganization ? <RequireOrganization>{guarded}</RequireOrganization> : guarded;
};

/**
 * Menu ogesi -> rota. Ekran `SCREENS`'te kayitliysa o cizilir; degilse yer tutucu.
 * Boylece bir feature'i hayata gecirmek bu dosyaya DOKUNMADAN mumkun (bkz. screens.ts).
 */
const zoneChildren = (zone: Zone): RouteObject[] => {
  const fromMenu = ZONE_MENUS[zone].map((item) => {
    const Screen = SCREENS[item.id];
    const content = Screen ? <Screen /> : <PlaceholderPage titleKey={item.labelKey} />;
    const element = withItemGuard(item, content);
    const path = relativePath(zone, item);
    return path === null ? { index: true, element } : { path, element };
  });

  // Menude gorunmeyen detay rotalari (/talepler/:taskId gibi). Bolge guard'ini
  // gruptan miras alirlar; kendi yetki kisitlari varsa ret paneliyle sarilir.
  //
  // withItemGuard yeniden kullanilmiyor: sahte bir MenuItem uretmek
  // exactOptionalPropertyTypes altinda `permission?: Code` ile `Code | undefined`
  // arasindaki farka takiliyor ve okuması da zor. Iki satirlik dallanma daha durust.
  const extras = (EXTRA_ROUTES[zone] ?? []).map((route) => {
    const content = <route.component />;
    let element: ReactNode = content;
    if (route.adminOnly) element = <RequireOrgAdmin>{content}</RequireOrgAdmin>;
    else if (route.permission) {
      element = <RequirePermission code={route.permission}>{content}</RequirePermission>;
    }
    const base = ZONE_PATHS[zone];
    const path = route.path.startsWith(`${base}/`)
      ? route.path.slice(base.length + 1)
      : route.path;
    return { path, element };
  });

  return [...fromMenu, ...extras];
};

const zoneRoute = (zone: Zone): RouteObject => ({
  path: ZONE_PATHS[zone],
  element: (
    <RequireAuth>
      <RequireFreshPassword>
        <RequireConsent>
          <RequireZone zone={zone}>
            <AppShell menu={ZONE_MENUS[zone]} zoneTitleKey={ZONE_TITLE_KEYS[zone]} />
          </RequireZone>
        </RequireConsent>
      </RequireFreshPassword>
    </RequireAuth>
  ),
  children: zoneChildren(zone),
});

export const router = createBrowserRouter(
  [
    { path: '/', element: <RootRedirect /> },
    ...ZONES.map(zoneRoute),
    // Kapi ekranlari da ust cubugu ve tema secicisini kullanir; AppShell menu
    // almazsa kenar cubugunu hic cizmez — bu yuzden ikinci bir kabuk yok.
    {
      element: <AppShell />,
      children: [
        { path: '/giris', element: <LoginScreen /> },
        {
          // OTURUM ISTER, KAPILAR CALISMAZ. RequireFreshPassword ve
          // RequireConsent burada YOK — bu iki rota onlarin hedefi; zincire
          // dahil edilselerdi kendilerine sonsuz yonlendirme yaparlardi.
          element: (
            <RequireAuth>
              <Outlet />
            </RequireAuth>
          ),
          children: [
            { path: '/ilk-giris', element: <FirstLoginSetup /> },
            { path: '/sozlesmeler', element: <ConsentWizard /> },
          ],
        },
        { path: '*', element: <NotFoundRoute /> },
      ],
    },
  ],
  { basename: ROUTER_BASENAME },
);
